<script setup>
    import { onMounted, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import { useHead } from '@vueuse/head';
    import Foot from '../components/Foot';
    import HeadBar from '../components/HeadBar';
    import Share from '../components/Share';
    import { imageUrl } from '../composables/http';
    import useBlog from '../composables/useBlog';
    import { isNullOrEmpty, toDateFormat } from '../composables/utils';

    const { slug } = defineProps({
        slug: {
            type: String,
            required: true
        }
    });

    const { loading, info, read, readRecent, rencentList, generateStructuredData } = useBlog();

    useHead(() => {
        if (!info.value) return {};

        return {
            title: `${info.value.Title} - Contractly`,
            meta: [
                {
                    name: 'description',
                    content: info.value.MetaDescription || info.value.Title
                },
                { name: 'keywords', content: info.value.Tags || '' },
                // Open Graph
                { property: 'og:title', content: info.value.Title },
                { property: 'og:description', content: info.value.MetaDescription || info.value.Title },
                { property: 'og:type', content: 'article' },
                { property: 'og:url', content: window.location.href },
                { property: 'og:image', content: imageUrl(info.value.Photo, 1200, 0, '', 'max') },
                // Twitter Card
                { name: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', content: info.value.Title },
                { name: 'twitter:description', content: info.value.MetaDescription || info.value.Title },
                { name: 'twitter:image', content: imageUrl(info.value.Photo, 1200, 0, '', 'max') }
            ],
            link: [
                { rel: 'canonical', href: window.location.href }
            ],
            script: [
                {
                    type: 'application/ld+json',
                    innerHTML: JSON.stringify(generateStructuredData(info.value))
                }
            ]
        }
    });

    onMounted(() => {
        read(slug);
        readRecent(6);
      
    });

    watch(info, (newVal) => {
        if (newVal) {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.text = JSON.stringify(generateStructuredData(newVal));
            document.head.appendChild(script);
        }
    });
</script>

<template>
    <div class="home">
        <HeadBar />
        <div class="container mt-12 blog">
            <div v-if="info!=null" class="max-w-screen-xl mx-auto" style="background-color: white;">
                <div class="space-y-6 md:p-8 p-2 mt-2 min-h-[500px]">
                    <n-breadcrumb style="overflow: hidden;">
                        <n-breadcrumb-item href="/">Home</n-breadcrumb-item>
                        <n-breadcrumb-item href="/blog">Blog</n-breadcrumb-item>
                        <n-breadcrumb-item>{{info?.Title}}</n-breadcrumb-item>
                    </n-breadcrumb>

                    <div class="flex md:flex-row flex-wrap">
                        <div class="w-full md:w-3/4 md:pr-6">
                            <article class="p-8 shadow-md rounded border mb-8">
                                <h1 class="text-lg sm:text-xl md:text-3xl font-bold text-gray-800 mb-4">{{info.Title}}</h1>
                                <div class="text-sm text-gray-500 mb-2">
                                    Published on {{toDateFormat(info.CreatedTime,'MMM DD, YYYY')}}
                                </div>

                                <img :src="imageUrl(info.Photo,1200,0,'','max')" class="object-cover w-full h-full rounded" :alt="info.Title" loading="lazy">

                                <div v-html="info.Content" class="default-margin text-lg mt-4"></div>

                                <Share v-if="info!=null" :title="info?.Title" />
                            </article>
                        </div>


                        <div class="w-full md:w-1/4 " v-if="rencentList!=null">
                            <n-card v-for="obj in rencentList.filter(m=>m.Id!=slug)" :key="`blog-${obj.Id}`" class="shadow-sm cursor-pointer hover:bg-slate-50 p-0 mb-4" size="small" hoverable>
                                <a :href="`/blog/${obj.Id}`">

                                    <div class="overflow-hidden h-[160px] w-full mt-2">
                                        <img :src="imageUrl(obj.Photo,560,0,'','max')" class="object-cover w-full h-full" :alt="obj.Title">
                                    </div>
                                    <div class="mt-2" style="display: flex; gap: 4px;">
                                        <div class="h-[62px] text-ellipsis overflow-hidden" style="flex: 1; font-size: 12px;">{{obj.Title}}</div>
                                        <div class="bold" style="font-size: 12px;">{{toDateFormat(obj.CreatedTime,'MMM DD, YYYY')}}</div>
                                    </div>
                                </a>
                            </n-card>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Foot />
        <CommonInfo ref="terms"></CommonInfo>
    </div>
</template>

<style>
  
</style>